import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Trash2, Upload, Paperclip, Milestone as MilestoneIcon, Save } from 'lucide-react';
import api, { API } from '../../api';
import { useToast } from '../../hooks/use-toast';

const STATUS_OPTS = [
  { v: 'upcoming', l: 'À venir' },
  { v: 'in_progress', l: 'En cours' },
  { v: 'done', l: 'Terminé' },
];
const STATUS_STYLE = {
  upcoming: 'bg-gray-200 text-gray-600',
  in_progress: 'bg-amber-100 text-amber-800 border border-amber-400',
  done: 'bg-green-100 text-green-700 border border-green-400',
};

export default function MilestonesManager({ rfqId }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const fileRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/rfq/${rfqId}/milestones`);
      setItems(res.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rfqId]);

  const startEdit = (m) => setEditing({ ...editing, [m.id]: { ...m } });
  const cancelEdit = (id) => { const e = { ...editing }; delete e[id]; setEditing(e); };
  const change = (id, key, val) => setEditing({ ...editing, [id]: { ...editing[id], [key]: val } });
  const save = async (id) => {
    const patch = editing[id];
    try {
      await api.put(`/admin/milestones/${id}`, {
        title: patch.title, status: patch.status,
        dueDate: patch.dueDate || '', completedDate: patch.completedDate || '',
        notes: patch.notes || '', order: patch.order || 0,
      });
      cancelEdit(id); load();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde impossible', duration: 10000 }); }
  };

  const addOne = async () => {
    try {
      await api.post(`/admin/rfq/${rfqId}/milestones`, {
        title: 'Nouveau jalon', status: 'upcoming', order: items.length,
      });
      load();
    } catch (e) { /* ignore */ }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer ce jalon ?')) return;
    try { await api.delete(`/admin/milestones/${id}`); load(); } catch (e) { /* ignore */ }
  };
  const attach = async (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/admin/milestones/${id}/attach`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Pièce jointe ajoutée', description: file.name, duration: 6000 });
      load();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Upload impossible', duration: 10000 }); }
  };
  const detach = async (mid, aid) => {
    try { await api.delete(`/admin/milestones/${mid}/attachment/${aid}`); load(); } catch (e) { /* ignore */ }
  };

  if (loading) return <div className="text-xs text-gray-500 flex items-center gap-2 pt-2 border-t border-gray-100"><Loader2 className="w-3 h-3 animate-spin" /> Chargement…</div>;

  return (
    <div className="pt-4 border-t border-gray-100 space-y-3" data-testid="milestones-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MilestoneIcon className="w-4 h-4 text-brand-navy" />
          <h5 className="font-bold text-brand-navy text-sm">Suivi de production · {items.length} jalons</h5>
        </div>
        <button onClick={addOne} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-gray-300 text-brand-navy hover:border-brand-navy" data-testid="add-milestone-btn">
          <Plus className="w-3 h-3" /> Ajouter
        </button>
      </div>

      <ol className="space-y-2">
        {items.map((m) => {
          const e = editing[m.id];
          return (
            <li key={m.id} className="bg-gray-50 rounded-lg p-3 text-xs" data-testid={`admin-milestone-${m.id}`}>
              {e ? (
                <div className="space-y-2">
                  <input value={e.title} onChange={(ev) => change(m.id, 'title', ev.target.value)} className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-semibold text-brand-navy" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select value={e.status} onChange={(ev) => change(m.id, 'status', ev.target.value)} className="px-2 py-1 border border-gray-200 rounded text-xs">
                      {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                    <input type="date" value={e.dueDate || ''} onChange={(ev) => change(m.id, 'dueDate', ev.target.value)} className="px-2 py-1 border border-gray-200 rounded text-xs" placeholder="Échéance" />
                    <input type="date" value={e.completedDate || ''} onChange={(ev) => change(m.id, 'completedDate', ev.target.value)} className="px-2 py-1 border border-gray-200 rounded text-xs" placeholder="Complété" />
                  </div>
                  <textarea rows={2} value={e.notes || ''} onChange={(ev) => change(m.id, 'notes', ev.target.value)} placeholder="Notes (visibles par le client)" className="w-full px-2 py-1 border border-gray-200 rounded text-xs" />
                  <div className="flex gap-2">
                    <button onClick={() => save(m.id)} className="inline-flex items-center gap-1 px-3 py-1 rounded bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400" data-testid={`save-milestone-${m.id}`}>
                      <Save className="w-3 h-3" /> Enregistrer
                    </button>
                    <button onClick={() => cancelEdit(m.id)} className="px-3 py-1 rounded border border-gray-300 text-xs">Annuler</button>
                    <button onClick={() => remove(m.id)} className="ml-auto px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-milestone-${m.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${STATUS_STYLE[m.status] || STATUS_STYLE.upcoming}`}>{STATUS_OPTS.find((o) => o.v === m.status)?.l || m.status}</span>
                      <span className="font-semibold text-brand-navy">{m.title}</span>
                    </div>
                    <button onClick={() => startEdit(m)} className="text-[11px] px-2 py-0.5 rounded border border-gray-300 text-brand-navy hover:border-brand-navy" data-testid={`edit-milestone-${m.id}`}>Modifier</button>
                  </div>
                  <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
                    {m.dueDate && <span>Échéance : {m.dueDate}</span>}
                    {m.completedDate && <span className="text-green-600 font-semibold">Complété : {m.completedDate}</span>}
                  </div>
                  {m.notes && <p className="text-gray-600 whitespace-pre-line">{m.notes}</p>}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {m.attachments?.map((a) => (
                      <span key={a.id} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-white border border-gray-200">
                        <a href={`${API}/admin/milestones/${m.id}/attachment/${a.id}`} target="_blank" rel="noreferrer" className="text-brand-navy hover:text-brand-yellow"><Paperclip className="w-3 h-3 inline" /> {a.filename}</a>
                        <button onClick={() => detach(m.id, a.id)} className="text-red-400 hover:text-red-600 ml-1"><Trash2 className="w-3 h-3" /></button>
                      </span>
                    ))}
                    <input ref={(el) => (fileRefs.current[m.id] = el)} type="file" className="hidden" onChange={(ev) => { const f = ev.target.files?.[0]; if (f) attach(m.id, f); ev.target.value = ''; }} />
                    <button onClick={() => fileRefs.current[m.id]?.click()} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-dashed border-gray-300 text-brand-navy hover:border-brand-navy" data-testid={`attach-milestone-${m.id}`}>
                      <Upload className="w-3 h-3" /> Ajouter pièce jointe
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
